import { Users, TrendingUp, Target } from "lucide-react";
import moment from "moment";

import LineChartVersion2 from "./Base/Charts/LineChartVersion2";
import { Tooltip } from "./Tooltip";
import { useState } from "react";

export const InfluencerAdvancedInsights = ({ userDatas }) => {
  const [showAll] = useState(false);

  const visibleUsers = showAll
    ? userDatas?.similar_user
    : userDatas?.similar_user?.slice(0, 4);

  const statHistoryData =
    userDatas?.stat_history?.map((item) => ({
      name: moment(item.month).format("MMM"),
      followers: item.followers,
      following: item.following,
      likes: item.avg_likes,
    })) || [];
  console.log(
    "🚀 ~ InfluencerAdvancedInsights ~ statHistoryData:",
    statHistoryData
  );

  const TopicTensor = userDatas?.relevant_tag || [];

  return (
    <section className="mb-8 px-4 sm:px-6">
      {/* Header */}
      <div className="text-left mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Advanced Insights
        </h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Topic Analysis */}
        <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                TopicTensor®
                <Tooltip description="We analyze content themes and topics based on caption data." />
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {TopicTensor?.length || 0} topics identified
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap gap-3 min-h-[200px] items-center justify-center p-4 ">
              {TopicTensor?.slice(0, 16)?.map((item, index) => {
                const fontSize = Math.max(14, Math.min(28, item.freq * 1.5));
                const opacity = Math.max(0.6, Math.min(1, item.freq * 0.8));
                return (
                  <div
                    key={item.id}
                    className="px-3 py-2 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 border border-green-200"
                    style={{
                      fontSize: `${fontSize}px`,
                      opacity: opacity,
                      background: `rgba(16, 185, 129, ${
                        0.1 + item.freq * 0.1
                      })`,
                    }}
                  >
                    <span className="font-medium text-white">{item.tag}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Lookalikes */}
        {userDatas?.similar_user?.length > 0 && (
          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  Lookalikes (similar topics)
                  <Tooltip description="Accounts with similar content topics and audience demographics" />
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {userDatas?.similar_user?.length} similar profiles found
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {visibleUsers?.map((account, index) => (
                <div key={index} className="flex items-center gap-4">
                  <img
                    src={account?.profile_pic_url}
                    alt={account?.similar_username}
                    className="w-12 h-12 rounded-full object-cover border-2 border-green-200 group-hover:border-green-400 transition-colors"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors">
                      @{account?.similar_username}
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                      {account?.fullname}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* {userDatas?.similar_user?.length > 4 && (
              <div className="mt-6 pt-4 border-t border-green-100">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="w-full py-3 px-4 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-200 shadow-lg shadow-green-500/25 hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  {showAll ? "Show Less" : `View All ${userDatas?.similar_user?.length} Profiles`}
                </button>
              </div>
            )} */}
          </div>
        )}
      </div>

      {/* Growth Charts */}
      {statHistoryData?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <h4 className="font-bold text-gray-900">Followers</h4>
              </div>
            </div>
            <div className="w-full">
              <LineChartVersion2
                data={statHistoryData}
                xAxisDataKey="name"
                lineDataKeys={["followers"]}
                colors={["#10b981"]}
                height={200}
                showGrid={true}
                showLegend={false}
                showTooltip={true}
                gridProps={{
                  horizontal: true,
                  vertical: false,
                  strokeDasharray: "0",
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <h4 className="font-bold text-gray-900">Following </h4>
              </div>
            </div>
            <div className="w-full">
              <LineChartVersion2
                data={statHistoryData}
                xAxisDataKey="name"
                lineDataKeys={["following"]}
                colors={["#059669"]}
                height={200}
                showGrid={true}
                showLegend={false}
                showTooltip={true}
                gridProps={{
                  horizontal: true,
                  vertical: false,
                  strokeDasharray: "0",
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-sm hover:shadow-lg transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <h4 className="font-bold text-gray-900">Avg Likes</h4>
              </div>
            </div>
            <div className="w-full">
              <LineChartVersion2
                data={statHistoryData}
                xAxisDataKey="name"
                lineDataKeys={["likes"]}
                colors={["#047857"]}
                height={200}
                showGrid={true}
                showLegend={false}
                showTooltip={true}
                gridProps={{
                  horizontal: true,
                  vertical: false,
                  strokeDasharray: "0",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
